import { useState } from 'react';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Button';
import { Avatar, AvatarImage, AvatarFallback } from '../../../components/Avatar';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/Popover';
import { useEngagementStore, EngagementComment, TodayCelebrations } from '../../../store/engagementStore';
import { getUserAvatarUrl } from '../../../utils/avatar';
import { Cake, PartyPopper, UserPlus, CalendarHeart, Award, MessageCircle, Send, SmilePlus } from 'lucide-react';
import toast from 'react-hot-toast';

// =======================================================================
//  Shared celebration "wish" kit — one source of truth for the dashboard
//  banner and the People → Engagement feed: the elaborated wish text, the
//  emoji reactions and the comment thread.
// =======================================================================

export type WishKind = 'birthday' | 'anniversary' | 'new_joiner' | 'festival' | 'award';

export interface WishItem {
  key: string;
  kind: WishKind;
  title: string;
  subtitle?: string;
  name: string;
  firstName: string;
  photoUrl?: string | null;
  years?: number;
  festivalName?: string;
  awardTitle?: string;
  description?: string;
  bannerUrl?: string | null;
  wishText?: string | null;
  templateBody?: string | null;
  eventType: string;
  eventKey: string;
  eventDate: string;
  reactions: Record<string, number>;
  myReactions: string[];
  commentCount: number;
}

export const KIND_META: Record<WishKind, { label: string; icon: typeof Cake; accent: string; grad: string; chip: string }> = {
  birthday: { label: 'Birthday', icon: Cake, accent: 'text-info', grad: 'from-info/15', chip: 'bg-info/10 text-info' },
  anniversary: { label: 'Work anniversary', icon: PartyPopper, accent: 'text-warning-strong', grad: 'from-warning/15', chip: 'bg-warning/10 text-warning-strong' },
  new_joiner: { label: 'Welcome', icon: UserPlus, accent: 'text-success-strong', grad: 'from-success/15', chip: 'bg-success/10 text-success-strong' },
  festival: { label: 'Festival', icon: CalendarHeart, accent: 'text-primary', grad: 'from-primary/15', chip: 'bg-primary/10 text-primary' },
  award: { label: 'Spotlight', icon: Award, accent: 'text-warning-strong', grad: 'from-warning/15', chip: 'bg-warning/10 text-warning-strong' },
};

export const EMOJIS = ['🎉', '❤️', '👏', '🎂'];

export const initials = (name: string) =>
  name.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '🎉';

/** Build the WishItem list the UI renders from the raw /today payload. */
export function toWishItems(today: TodayCelebrations | null): WishItem[] {
  if (!today) return [];
  const out: WishItem[] = [];
  const base = (x: any) => ({
    eventType: x.eventType, eventKey: x.eventKey, eventDate: x.eventDate,
    reactions: x.reactions || {}, myReactions: x.myReactions || [], commentCount: x.commentCount || 0,
    templateBody: x.templateBody ?? null,
  });
  for (const b of today.birthdays || [])
    out.push({ key: `b-${b.employeeId}`, kind: 'birthday', title: `Happy Birthday, ${b.name}!`, subtitle: b.designation || b.department || undefined, name: b.name, firstName: b.firstName || b.name.split(' ')[0], photoUrl: b.photoUrl, ...base(b) });
  for (const a of today.anniversaries || []) {
    const y = a.years ? `${a.years} year${a.years === 1 ? '' : 's'}` : '';
    out.push({ key: `a-${a.employeeId}`, kind: 'anniversary', title: `${a.name} — ${y} with us!`, subtitle: 'Happy work anniversary 🎊', name: a.name, firstName: a.firstName || a.name.split(' ')[0], photoUrl: a.photoUrl, years: a.years, ...base(a) });
  }
  for (const n of today.newJoiners || [])
    out.push({ key: `n-${n.employeeId}`, kind: 'new_joiner', title: `Welcome aboard, ${n.name}!`, subtitle: n.designation || 'New team member', name: n.name, firstName: n.firstName || n.name.split(' ')[0], photoUrl: n.photoUrl, ...base(n) });
  for (const f of today.festivals || [])
    out.push({ key: `f-${f.id}`, kind: 'festival', title: `Happy ${f.name}!`, subtitle: f.description || 'Wishing everyone joy', name: f.name, firstName: 'Team', festivalName: f.name, bannerUrl: f.bannerUrl, wishText: f.wishText, ...base(f) });
  for (const w of today.awards || [])
    out.push({ key: `w-${w.id}`, kind: 'award', title: w.employeeName ? `${w.employeeName} — ${w.title}` : w.title, subtitle: w.description || 'Congratulations! 🏆', name: w.employeeName || w.title, firstName: (w.employeeName || 'Team').split(' ')[0], awardTitle: w.title, description: w.description, photoUrl: w.photoUrl, ...base(w) });
  return out;
}

export interface Wish { greeting: string; paragraphs: string[]; signoff: string[] }

/** Default wish body per occasion. Placeholders: {name} {first_name} {years} {festival} {award}. */
export const DEFAULT_WISH_BODIES: Record<WishKind, string> = {
  birthday:
    "Wishing you a very Happy Birthday! 🎂\nMay your day be filled with happiness, laughter and everything you love. We're so glad to have you with us, and we hope the year ahead brings you great health, success and joy.",
  anniversary:
    "Congratulations on completing {years} with us! 🎊\nThank you for your dedication, energy and the difference you make every day. Here's to many more wonderful years together.",
  new_joiner:
    "A very warm welcome to the team! 👋\nWe're thrilled to have you on board and can't wait to achieve great things together. Wishing you an amazing journey ahead.",
  festival:
    "Wishing you and your loved ones a very happy {festival}! ✨\nMay this celebration bring joy, peace and prosperity to you and your family.",
  award:
    "Congratulations on {award}! 🏆\nYour hard work and commitment truly stand out. Thank you for setting an example — this recognition is very well deserved.",
};

/** Fill the placeholders in a wish body from the item. */
export function substituteWish(body: string, item: WishItem): string {
  const years = item.years ? `${item.years} year${item.years === 1 ? '' : 's'}` : '';
  return body
    .split('{name}').join(item.name || '')
    .split('{first_name}').join(item.firstName || 'there')
    .split('{years}').join(years || 'your work anniversary')
    .split('{festival}').join(item.festivalName || 'the festival')
    .split('{award}').join(item.awardTitle || 'your recognition');
}

const greetingFor = (item: WishItem) =>
  item.kind === 'festival' || !item.firstName || item.firstName === 'Team'
    ? 'Dear Team,'
    : `Dear ${item.firstName},`;

/**
 * The elaborated, letter-style wish. Uses, in order: a festival's own custom
 * wish, the admin-edited template for the occasion, then the built-in default.
 */
export function buildWish(item: WishItem): Wish {
  const raw = (item.kind === 'festival' && item.wishText?.trim())
    ? item.wishText.trim()
    : (item.templateBody?.trim() || DEFAULT_WISH_BODIES[item.kind]);
  const paragraphs = substituteWish(raw, item).split('\n').map((s) => s.trim()).filter(Boolean);
  return { greeting: greetingFor(item), paragraphs, signoff: ['Warm regards,', 'Trusty Bytes HR Team'] };
}

/** The Trusty Bytes wordmark in the brand colours, shown at the top of each wish. */
export function WishBrandHeader({ className = '' }: { className?: string }) {
  return (
    <span className={`font-display text-sm font-bold tracking-tight ${className}`}>
      <span style={{ color: '#0062D2' }}>Trusty</span>{' '}
      <span style={{ color: '#8CC63F' }}>Bytes</span>
    </span>
  );
}

/** Renders the letter-style wish. */
export function WishLetter({ item, compact }: { item: WishItem; compact?: boolean }) {
  const w = buildWish(item);
  return (
    <div className={compact ? 'space-y-1.5 text-sm' : 'space-y-2 text-sm'}>
      <p className="font-medium text-foreground">{w.greeting}</p>
      {(compact ? w.paragraphs.slice(0, 1) : w.paragraphs).map((p, i) => (
        <p key={i} className="text-muted-foreground leading-relaxed">{p}</p>
      ))}
      <div className="pt-0.5 text-muted-foreground">
        {w.signoff.map((s, i) => <p key={i} className={i === 0 ? '' : 'font-medium text-foreground'}>{s}</p>)}
      </div>
    </div>
  );
}

// ---- Reactions ---------------------------------------------------------

export function ReactionBar({ item }: { item: WishItem }) {
  const { toggleReaction } = useEngagementStore();
  const [reactions, setReactions] = useState<Record<string, number>>(item.reactions || {});
  const [mine, setMine] = useState<string[]>(item.myReactions || []);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const react = async (emoji: string) => {
    if (busy || !emoji) return;
    setBusy(true);
    try {
      const res = await toggleReaction({ eventType: item.eventType, eventKey: item.eventKey, eventDate: item.eventDate, emoji });
      setReactions(res.reactions || {});
      setMine(res.myReactions || []);
    } catch {
      toast.error('Could not react');
    } finally {
      setBusy(false);
    }
  };

  // Always show the quick presets, plus any other emoji someone has already used.
  const shown = Array.from(new Set([...EMOJIS, ...Object.keys(reactions).filter((e) => (reactions[e] || 0) > 0)]));

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((e) => {
        const count = reactions[e] || 0;
        const active = mine.includes(e);
        return (
          <button
            key={e}
            onClick={() => react(e)}
            disabled={busy}
            className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-sm transition-colors ${
              active ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card hover:bg-secondary'
            }`}
          >
            <span>{e}</span>
            {count > 0 && <span className="text-xs tabular-nums">{count}</span>}
          </button>
        );
      })}

      {/* Pick any emoji */}
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <button
            className="inline-flex size-8 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label="More emojis"
          >
            <SmilePlus className="size-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto border-0 p-0 shadow-lg" align="start">
          <EmojiPicker
            onEmojiClick={(d: EmojiClickData) => { setPickerOpen(false); react(d.emoji); }}
            theme={Theme.AUTO}
            width={300}
            height={360}
            lazyLoadEmojis
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// ---- Comments ----------------------------------------------------------

export function CommentsSection({ item, startOpen }: { item: WishItem; startOpen?: boolean }) {
  const { listComments, addComment } = useEngagementStore();
  const [open, setOpen] = useState(!!startOpen);
  const [comments, setComments] = useState<EngagementComment[]>([]);
  const [count, setCount] = useState(item.commentCount || 0);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const ev = { eventType: item.eventType, eventKey: item.eventKey, eventDate: item.eventDate };

  const load = async () => {
    setLoading(true);
    try { setComments(await listComments(ev)); setLoaded(true); } catch { /* ignore */ } finally { setLoading(false); }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && count > 0) await load();
  };

  const submit = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const c = await addComment({ ...ev, body: text.trim() });
      setComments((p) => [...p, c]);
      setCount((n) => n + 1);
      setText('');
      setLoaded(true);
      setOpen(true);
    } catch {
      toast.error('Could not post comment');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-2">
      <button onClick={toggle} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
        <MessageCircle className="size-3.5" />
        {count > 0 ? `${count} comment${count === 1 ? '' : 's'}` : 'Comment'}
      </button>

      {open && (
        <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
          {loading && <p className="text-xs text-muted-foreground">Loading…</p>}
          {comments.map((c) => (
            <div key={c.id} className="text-sm">
              <span className="font-medium text-foreground">{c.author}</span>
              <span className="ml-2 text-muted-foreground">{c.body}</span>
            </div>
          ))}
          {!loading && comments.length === 0 && <p className="text-xs text-muted-foreground">Be the first to leave a wish.</p>}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          placeholder="Write a wish…"
          className="h-9"
        />
        <Button size="icon-sm" className="size-9" onClick={submit} disabled={sending || !text.trim()} aria-label="Post wish">
          <Send className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The photo / icon shown beside a wish. For an employee with an uploaded photo
 * it shows the FULL image (uncropped, object-contain) so portraits are not cut
 * into a circle; festivals and people without a photo fall back to an icon /
 * generated avatar.
 */
export function WishMedia({ item, size = 'md' }: { item: WishItem; size?: 'sm' | 'md' | 'lg' }) {
  const Meta = KIND_META[item.kind];
  const Icon = Meta.icon;
  const dim = size === 'lg' ? 'size-28' : size === 'md' ? 'size-24' : 'size-20';
  const cap = size === 'lg' ? 'max-h-72' : size === 'md' ? 'max-h-56' : 'max-h-40';

  // Festival: show the uploaded banner (full) if there is one, else an icon.
  if (item.kind === 'festival') {
    if (item.bannerUrl) {
      return (
        <img
          src={item.bannerUrl}
          alt={item.name}
          className={`${cap} w-auto max-w-full rounded-xl object-contain shadow-sm`}
        />
      );
    }
    return (
      <div className={`flex ${dim} items-center justify-center rounded-full bg-card/80 shadow-sm ring-4 ring-card`}>
        <Icon className={`size-10 ${Meta.accent}`} />
      </div>
    );
  }
  // Employee with an uploaded photo: show the full image uncropped.
  if (item.photoUrl) {
    return (
      <img
        src={item.photoUrl}
        alt={item.name}
        className={`${cap} w-auto max-w-full rounded-xl object-contain shadow-sm`}
      />
    );
  }
  return (
    <Avatar className={`${dim} ring-4 ring-card`}>
      <AvatarImage src={getUserAvatarUrl(item.name)} alt={item.name} />
      <AvatarFallback className="text-2xl">{initials(item.name)}</AvatarFallback>
    </Avatar>
  );
}

/**
 * Wish card — media on the right, letter + interactions on the left.
 * `compact` shrinks it (smaller photo, condensed wish) for the dashboard grid;
 * the full size is used on the Engagement feed.
 */
export function WishCard({ item, compact }: { item: WishItem; compact?: boolean }) {
  const Meta = KIND_META[item.kind];
  const Icon = Meta.icon;
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-xs sm:flex-row">
      <div className={`order-2 flex-1 space-y-3 sm:order-1 ${compact ? 'p-4' : 'p-5'}`}>
        <div className="flex items-center justify-between gap-2 border-b border-border pb-2.5">
          <WishBrandHeader />
          <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium uppercase tracking-wide ${Meta.chip}`}>
            <Icon className="size-3.5" /> {Meta.label}
          </div>
        </div>
        <WishLetter item={item} compact={compact} />
        <ReactionBar item={item} />
        <CommentsSection item={item} />
      </div>
      <div className={`order-1 flex items-center justify-center bg-gradient-to-br ${Meta.grad} to-card sm:order-2 sm:shrink-0 ${compact ? 'p-3 sm:w-1/3' : 'p-4 sm:w-2/5'}`}>
        <WishMedia item={item} size={compact ? 'sm' : 'lg'} />
      </div>
    </div>
  );
}
